import React, { useState, useEffect } from "react"
import axios from 'axios';
import { toast } from 'react-hot-toast';

const BookingModal = ({ showModal, lawyerName, bookingForm, onClose, onChange, onSubmit, userData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState({hour: "06", minute: "00", period: "AM"});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Check if fields are pre-filled from user data
  const preFilledFields = {
    name: userData && (userData.name || userData.fullName) && bookingForm.name === (userData.name || userData.fullName),
    phone: userData && userData.phone && bookingForm.phone === userData.phone
  };
  const [cautionConfirmed, setCautionConfirmed] = useState(false);
  
  // Track if fields are pre-filled from user data (handled by parent component now)
  
  if (!showModal) return null;
  
  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };
  
  // Handle time selection
  const handleTimeChange = (type, field, value) => {
    if (type === "start") {
      const newTime = {...startTime, [field]: value};
      setStartTime(newTime);
      
      // Clear time error if the time is valid
      if (errors.time && !isTimeInPastWithTime(newTime)) {
        setErrors(prev => ({...prev, time: undefined}));
      }
    }
  };
  
  // Navigation between form steps
  const nextStep = () => {
    // Validate first step
    if (currentStep === 1) {
      const newErrors = {};
      
      if (!bookingForm.name.trim()) {
        newErrors.name = "Name is required";
      }
      
      if (!bookingForm.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!/^[\d\s+()-]{10,15}$/.test(bookingForm.phone.trim())) {
        newErrors.phone = "Please enter a valid phone number";
      }
      
      if (!bookingForm.legalArea?.trim()) {
        newErrors.legalArea = "Legal service area is required";
      }
      
      if (!bookingForm.description?.trim()) {
        newErrors.description = "Please describe your legal needs";
      }
      
      if (!cautionConfirmed) {
        newErrors.cautionConfirmation = "You must confirm you understand the caution message";
      }
      
      setErrors(newErrors);
      
      if (Object.keys(newErrors).length === 0) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  // Date management functions
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const formatMonthYear = (date) => {
    const options = { month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };
  
  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = getDaysInMonth(year, month);
    
    const days = [];
    
    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month: month,
        year: year,
        date: new Date(year, month, i)
      });
    }
    
    return days;
  };
  
  // Check if date is in the past
  const isDateInPast = (date) => {
    const today = new Date();
    const checkDate = new Date(date.year, date.month, date.day);
    return checkDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };
  
  // Check if time is in the past on current day
  const isTimeInPast = () => {
    if (!selectedDate) return false;
    return isTimeInPastWithTime(startTime);
  };
  
  // Helper function to check if a specific time is in the past
  const isTimeInPastWithTime = (timeObj) => {
    if (!selectedDate) return false;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDateObj = new Date(selectedDate.year, selectedDate.month, selectedDate.day);
    
    // Only check for today
    if (selectedDateObj.getTime() !== today.getTime()) return false;
    
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Convert 12-hour format to 24-hour for comparison
    let selectedHour = parseInt(timeObj.hour, 10);
    if (timeObj.period === "PM" && selectedHour !== 12) {
      selectedHour += 12;
    }
    if (timeObj.period === "AM" && selectedHour === 12) {
      selectedHour = 0;
    }
    
    const selectedMinute = parseInt(timeObj.minute, 10);
    
    if (selectedHour < currentHour) {
      return true;
    } else if (selectedHour === currentHour && selectedMinute < currentMinute) {
      return true;
    }
    
    return false;
  };
  
  // Final form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!bookingForm.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!bookingForm.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s+()-]{10,15}$/.test(bookingForm.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }
    
    if (!bookingForm.legalArea?.trim()) {
      newErrors.legalArea = "Legal service area is required";
    }
    
    if (!bookingForm.description?.trim()) {
      newErrors.description = "Please describe your legal needs";
    }
    
    if (!selectedDate) {
      newErrors.date = "Please select a date";
    }
    
    if (!cautionConfirmed) {
      newErrors.cautionConfirmation = "You must confirm you understand the caution message";
    }
    
    // Validate time is not in the past
    if (isTimeInPast()) {
      newErrors.time = "Please select a future time";
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Combine date and time data with the form
    const dateString = selectedDate ? 
      `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}-${String(selectedDate.day).padStart(2, '0')}` : '';
    
    const bookingData = {
      ...bookingForm,
      date: dateString,
      startTime: `${startTime.hour}:${startTime.minute} ${startTime.period}`
    };
    
    try {
      // Get lawyer data (assuming it's available in the parent component)
      // This would be passed down as props or stored in context/state
      const lawyerId = bookingForm.lawyerId;
      const lawyerEmail = bookingForm.lawyerEmail;
      
      // Submit booking to the API
      await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/appointments/book`, {
        ...bookingData,
        lawyerId,
        lawyerName,
        lawyerEmail
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Appointment booked successfully! The solicitor will receive your details if they claim the listing.');
      
      // Reset form and close modal
      onSubmit(e, bookingData);
      onClose();
    } catch (error) {
      console.error('Error booking appointment:', error);
      
      if (error.response?.status === 401) {
        toast.error('You must be logged in to book an appointment. Please sign in and try again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to book appointment');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render minute buttons
  const renderMinuteButtons = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += 5) {
      minutes.push(String(i).padStart(2, '0'));
    }
    
    return minutes.map(minute => (
      <button
        key={minute}
        type="button"
        className={`w-8 h-8 text-sm rounded-md m-0.5 ${
          startTime.minute === minute 
            ? 'bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        onClick={() => handleTimeChange('start', 'minute', minute)}
      >
        {minute}
      </button>
    ));
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6 md:p-8 bg-black bg-opacity-60 backdrop-blur-sm overflow-auto" onClick={onClose}>
      <div 
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95%] sm:max-w-[700px] overflow-hidden animate-fadeIn my-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#301555] to-[#411C73] px-5 py-4 sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Book an Appointment
            </h3>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/30 rounded-full p-1"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-5 sm:p-6 max-h-[80vh] overflow-y-auto">
          <p className="text-gray-700 mb-5 text-sm sm:text-base">
            Please provide your details to book an appointment with <span className="font-medium">{lawyerName}</span>
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-5 mb-5">
              {currentStep === 1 ? (
                <>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Name {preFilledFields.name && <span className="ml-1 text-xs text-gray-500">(Pre-filled)</span>}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={bookingForm.name}
                      onChange={onChange}
                      required
                      className={`w-full p-2.5 sm:p-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm sm:text-base ${preFilledFields.name ? 'bg-gray-50' : ''}`}
                      placeholder="Enter your full name"
                      disabled={preFilledFields.name}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone Number {preFilledFields.phone && <span className="ml-1 text-xs text-gray-500">(Pre-filled)</span>}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={bookingForm.phone}
                      onChange={onChange}
                      required
                      className={`w-full p-2.5 sm:p-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm sm:text-base ${preFilledFields.phone ? 'bg-gray-50' : ''}`}
                      placeholder="Enter your phone number"
                      disabled={preFilledFields.phone}
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="legalArea" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Legal Service Area
                    </label>
                    <input
                      type="text"
                      id="legalArea"
                      name="legalArea"
                      value={bookingForm.legalArea || ''}
                      onChange={onChange}
                      required
                      className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm sm:text-base"
                      placeholder="e.g. Family Law, Immigration, etc."
                    />
                    {errors.legalArea && <p className="mt-1 text-sm text-red-600">{errors.legalArea}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Describe your legal needs
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows="4"
                      value={bookingForm.description || ''}
                      onChange={onChange}
                      required
                      className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm sm:text-base"
                      placeholder="Briefly describe what you need help with..."
                    ></textarea>
                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>Please Note!</strong> This appointment request will be shared with the solicitor. Do not include sensitive financial details or confidential information that requires legal privilege.
                      </span>
                    </p>
                    <div className="mt-3 flex items-center">
                      <input
                        type="checkbox"
                        id="cautionConfirmation"
                        checked={cautionConfirmed}
                        onChange={(e) => setCautionConfirmed(e.target.checked)}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="cautionConfirmation" className="ml-2 block text-sm text-amber-800">
                        I understand and confirm that I have not shared sensitive financial details or confidential information
                      </label>
                    </div>
                    {errors.cautionConfirmation && <p className="mt-1 text-sm text-red-600">{errors.cautionConfirmation}</p>}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Preferred Date/Time
                    </label>
                    
                    {/* Calendar Component */}
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1 mb-4 md:mb-0 border border-gray-200 rounded-xl overflow-hidden">
                        <div className="bg-gray-50 p-2 sm:p-3 flex justify-between items-center border-b border-gray-200">
                          <button 
                            type="button"
                            onClick={prevMonth}
                            className="text-gray-500 hover:text-gray-700 p-1"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                          </button>
                          <h4 className="text-sm sm:text-base text-gray-700 font-medium">
                            {formatMonthYear(currentMonth)}
                          </h4>
                          <button 
                            type="button"
                            onClick={nextMonth}
                            className="text-gray-500 hover:text-gray-700 p-1"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                          </button>
                        </div>
                        
                        <div className="p-2 sm:p-3">
                          <div className="grid grid-cols-7 gap-1 text-center mb-2">
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                              <div key={index} className="text-xs font-medium text-gray-500">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1 text-center">
                            {generateCalendarDays().map((day, index) => (
                              <div key={index} className="aspect-square">
                                {day ? (
                                  <button
                                    type="button"
                                    disabled={isDateInPast(day)}
                                    onClick={() => handleDateSelect(day)}
                                    className={`w-full h-full flex items-center justify-center text-sm rounded-md 
                                      ${selectedDate && selectedDate.day === day.day && 
                                      selectedDate.month === day.month && 
                                      selectedDate.year === day.year
                                        ? 'bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white'
                                        : isDateInPast(day)
                                          ? 'text-gray-300 cursor-not-allowed'
                                          : 'hover:bg-gray-100'
                                      }`}
                                  >
                                    {day.day}
                                  </button>
                                ) : (
                                  <div className="w-full h-full"></div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div className="sm:block sm:space-y-4">
                          <div>
                            <div className="text-center mb-1 sm:mb-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-700">Start Time</span>
                            </div>
                            <div className="bg-gray-50 p-2 sm:p-3 rounded-lg text-center">
                              <div className="text-lg sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">
                                {startTime.hour}:{startTime.minute} {startTime.period}
                              </div>
                              <div className="flex justify-center gap-1 sm:gap-2">
                                <select 
                                  value={startTime.hour}
                                  onChange={(e) => handleTimeChange('start', 'hour', e.target.value)}
                                  className={`p-0.5 sm:p-1 text-xs sm:text-sm border ${isTimeInPast() ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                                >
                                  {Array.from({ length: 12 }, (_, i) => String(i === 0 ? 12 : i).padStart(2, '0')).map(hour => (
                                    <option key={hour} value={hour}>{hour}</option>
                                  ))}
                                </select>
                                <select 
                                  value={startTime.minute}
                                  onChange={(e) => handleTimeChange('start', 'minute', e.target.value)}
                                  className={`p-0.5 sm:p-1 text-xs sm:text-sm border ${isTimeInPast() ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                                >
                                  {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(minute => (
                                    <option key={minute} value={minute}>{minute}</option>
                                  ))}
                                </select>
                                <select 
                                  value={startTime.period}
                                  onChange={(e) => handleTimeChange('start', 'period', e.target.value)}
                                  className={`p-0.5 sm:p-1 text-xs sm:text-sm border ${isTimeInPast() ? 'border-red-300' : 'border-gray-300'} rounded-md`}
                                >
                                  <option value="AM">AM</option>
                                  <option value="PM">PM</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                    {errors.time && <p className="mt-1 text-sm text-red-600">{errors.time}</p>}
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>
                        <strong>Please Note!</strong> This appointment request will be shared with the solicitor. Please ensure your contact information and availability are accurate. The solicitor will contact you to confirm the appointment. We will try to book an appointment close to this date and time, however the scheduled date and time is subject to solicitor availability.
                      </span>
                    </p>
                    <div className="mt-3 flex items-center">
                      <input
                        type="checkbox"
                        id="cautionConfirmation2"
                        checked={cautionConfirmed}
                        onChange={(e) => setCautionConfirmed(e.target.checked)}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="cautionConfirmation2" className="ml-2 block text-sm text-amber-800">
                        I understand and confirm that I have not shared sensitive financial details or confidential information
                      </label>
                    </div>
                    {errors.cautionConfirmation && <p className="mt-1 text-sm text-red-600">{errors.cautionConfirmation}</p>}
                  </div>
                </>
              )}
            </div>
            
            {currentStep === 1 ? (
              <div className="flex justify-end pt-3">
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-4 py-2.5 sm:py-3 bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:opacity-90 active:opacity-95 text-white font-medium rounded-lg transition-colors shadow-md flex items-center justify-center text-sm sm:text-base"
                >
                  Next
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  onClick={prevStep}
                  className="order-1 sm:order-1 px-4 py-2.5 sm:py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors flex items-center justify-center text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Back
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="order-2 sm:order-2 flex-1 px-4 py-2.5 sm:py-3 bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:opacity-90 active:opacity-95 text-white font-medium rounded-lg transition-colors shadow-md flex items-center justify-center text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"></path>
                  </svg>
                  {isSubmitting ? 'Booking...' : 'Book Now'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal; 